import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Button, 
  Divider, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import TeamGroup from './TeamGroup';
import { useCommonEventData, useEventOperationsData } from '../../contexts/EventContext';
import axios from 'axios';
import { getBackendUserUrl } from '../../utils/utils';
import Typography from '@mui/material/Typography';

const DEFAULT_TEAM_SECTIONS = [
  'Lead Hosts'
];

const TeamTab = () => {
  const { hostCompanyId, hostId, eventId } = useCommonEventData();
  const { addons } = useEventOperationsData();
  const [assignmentGroups, setAssignmentGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddRoleDialogOpen, setIsAddRoleDialogOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');

  const fetchAssignmentGroups = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${getBackendUserUrl()}/agenda/assignment_groups/${eventId}`);
      setAssignmentGroups(response.data);
    } catch (error) {
      console.error('Error fetching assignment groups:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchCompanyId = async () => {
      if (!hostCompanyId && hostId) {
        try {
          const response = await axios.get(`${getBackendUserUrl()}/user/${hostId}/company`);
          console.log('Fetched companyId:', response.data.companyId);
        } catch (error) {
          console.error('Error fetching companyId:', error);
        }
      }
    };

    fetchCompanyId();
    if (eventId) {
      fetchAssignmentGroups();
    }
  }, [eventId, hostId, hostCompanyId]);

  const handleAddRole = async () => {
    if (newRoleName.trim()) {
      try {
        const response = await axios.post(`${getBackendUserUrl()}/agenda/assignment_groups/`, {
          event_id: eventId,
          name: newRoleName.trim(),
          created_by: hostId
        });

        if (response.data) {
          await fetchAssignmentGroups(); // Refresh the groups after adding new one
          setNewRoleName('');
          setIsAddRoleDialogOpen(false);
        }
      } catch (error) {
        console.error('Error creating assignment group:', error);
      }
    }
  };

  const handleDeleteRole = async (assignmentGroupId) => {
    try {
      await axios.delete(`${getBackendUserUrl()}/agenda/assignment_groups/${assignmentGroupId}`);
      await fetchAssignmentGroups(); // Refresh the groups after deletion
    } catch (error) {
      console.error('Error deleting assignment group:', error);
    }
  };

  const handleAddMembers = async (selectedUsers, assignmentGroupId) => {
    try {
      const userIds = selectedUsers.map(user => user.userId);
      
      // Add each user to the assignment group
      const addMemberPromises = userIds.map(userId => 
        axios.post(`${getBackendUserUrl()}/agenda/assignment_group_members/`, {
          assignment_group_id: assignmentGroupId,
          user_id: userId
        })
      );

      await Promise.all(addMemberPromises);
      await fetchAssignmentGroups(); // Refresh the groups after adding members
    } catch (error) {
      console.error('Error adding members:', error);
    }
  };

  const handleEditGroupName = async (groupId, newName) => {
    try {
      const res = await axios.patch(
        `${getBackendUserUrl()}/agenda/assignment_groups/${groupId}`,
        { name: newName }
      );
      setAssignmentGroups(prevGroups => prevGroups.map(g =>
        g.assignmentGroupId === groupId ? { ...g, name: res.data.name } : g
      ));
    } catch (err) {
      console.error('Failed to update group name', err);
      // Optionally show error to user
    }
  };

  // Helper to generate vendor sections from addons
  console.log('addons here', addons)
  const vendorSections = addons
    ? addons.map(addon => ({
        name: addon.company.name + ' ' + addon.addonType || 'Vendor',
        isVendor: true
      }))
    : [];

  return (
    <Box sx={{ mt: 2, px: { xs: 0, sm: 2 } }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Build your event team
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Assign people to these roles and vendor groups to organize your event. Add new roles as needed.
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', mb: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          <Button 
            variant="outlined" 
            onClick={() => setIsAddRoleDialogOpen(true)}
            sx={{ 
              borderRadius: 8, 
              textTransform: 'none', 
              fontWeight: 600,
              width: 'fit-content',
              px: 2,
              py: 1
            }}
          >
            + Add Role
          </Button>
        </Box>
        <Divider />
      </Box>

      {/* Loading State */}
      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 6, mb: 6 }}>
          <Typography variant="h6" sx={{ color: '#64748b' }}>
            Loading team groups...
          </Typography>
        </Box>
      ) : (
        assignmentGroups.map((group, idx) => (
          <TeamGroup
            key={group.assignmentGroupId || group.name || idx}
            teamName={group.name}
            assignmentGroupId={group.assignmentGroupId}
            hostCompanyId={hostCompanyId}
            addons={addons}
            onDelete={group.assignmentGroupId ? () => handleDeleteRole(group.assignmentGroupId) : undefined}
            onAddMembers={group.assignmentGroupId ? (selectedUsers) => handleAddMembers(selectedUsers, group.assignmentGroupId) : undefined}
            onEditName={newName => handleEditGroupName(group.assignmentGroupId, newName)}
          />
        ))
      )}

      {/* Add Role Dialog */}
      <Dialog 
        open={isAddRoleDialogOpen} 
        onClose={() => setIsAddRoleDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: '400px'
          }
        }}
      >
        <DialogTitle>Add New Role</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Role Name"
            fullWidth
            variant="outlined"
            value={newRoleName}
            onChange={(e) => setNewRoleName(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{ style: { alignItems: 'center' } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setIsAddRoleDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button 
            onClick={handleAddRole} 
            variant="contained" 
            disabled={!newRoleName.trim()}
          >
            Add Role
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeamTab; 